import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import findAllByKey from '../utils/findAllByKey';
import { useProcessAlerts } from '../../../utils/EntityMetaDataUtil';
import { IAlert, IMetaAction } from '../../../graphql/schema/p4types';

export const useAlertsAndJobs = (
  name: string,
  values: any,
  addHeaderAlert: (alert: IAlert) => void,
  { keyToFind = 'entityMetaData' }: { keyToFind?: string } = {}
) => {
  const dispatch = useDispatch();
  const entityMetaData = useMemo(() => {
    if (values) {
      return findAllByKey(values, keyToFind);
    }
    return [];
  }, [keyToFind, values]);

  //Accumulate all the alert arrays
  const alertMetaData = useMemo(() => {
    return entityMetaData.reduce(
      (accumulator: Array<IAlert>, currentValue: { alerts: Array<IAlert> }) =>
        accumulator.concat(currentValue?.alerts || []),
      []
    );
  }, [entityMetaData]);
  //Accumulate all the action arrays.
  const actionMetaData = useMemo(() => {
    return entityMetaData.reduce(
      (accumulator: Array<IMetaAction>, currentValue: { actions: Array<IMetaAction> }) =>
        accumulator.concat(currentValue?.actions || []),
      []
    );
  }, [entityMetaData]);
  //Track which alerts/actions have been passed for processing, otherwise we may accidentally submit the same alert/job toast twice.
  const processedAlertUuids = useRef<Array<string>>([]);
  const processedActionJobIds = useRef<Array<string>>([]);

  const processAlerts = useProcessAlerts({ addHeaderAlert });
  useEffect(() => {
    const unprocessedAlerts = alertMetaData.filter(
      (alert: IAlert) => !processedAlertUuids.current.find((pa) => pa === alert.uuid)
    );
    //Only interested in processing job actions here.
    //Specific actions to be processed by the relevant form.
    const unprocessedJobActions = actionMetaData
      .filter((action: IMetaAction) => action.jobInfo)
      .filter((action: IMetaAction) => !processedActionJobIds.current.find((pa) => pa === action.jobInfo!.jobId));

    if (unprocessedAlerts?.length > 0) {
      unprocessedAlerts.forEach((alert: IAlert) => processedAlertUuids.current.push(alert.uuid!));
    }
    if (unprocessedJobActions?.length > 0) {
      unprocessedJobActions.forEach((action: IMetaAction) =>
        processedActionJobIds.current.push(action.jobInfo!.jobId!)
      );
    }
    //Call processAlerts regardless of whether we have any, since it also needs to proceess and display jobs.
    processAlerts({ actions: unprocessedJobActions, alerts: unprocessedAlerts }, name);
  }, [actionMetaData, addHeaderAlert, alertMetaData, dispatch, entityMetaData, name, processAlerts]);
};
